import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tmdbAPI, watchlistAPI } from '../services/api';
import MediaCard from '../components/MediaCard';
import { IOSButton } from '../components/ios';
import { HiOutlineFilm, HiOutlineCollection, HiOutlineTrendingUp } from 'react-icons/hi';

/**
 * Dashboard mobile - igual à página Buscar
 * Cards em scroll horizontal
 */
function MobileDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [stats, setStats] = useState({ movies: 0, series: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [trendingRes, watchlistRes] = await Promise.all([
        tmdbAPI.getTrending('all', 'week', 1),
        watchlistAPI.getAll(),
      ]);
      
      setTrending(trendingRes.data.results?.slice(0, 10) || []);
      setHasMore(trendingRes.data.total_pages > 1);
      setPage(1);

      const watchlist = watchlistRes.data;
      setStats({
        movies: watchlist.filter((i) => i.media_type === 'movie').length,
        series: watchlist.filter((i) => i.media_type === 'tv').length,
        total: watchlist.length,
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const trendingRes = await tmdbAPI.getTrending('all', 'week', nextPage);

      const newItems = trendingRes.data.results?.slice(0, 10) || [];
      setTrending((prev) => [...prev, ...newItems]);

      const totalPages = trendingRes.data.total_pages || 1;
      setHasMore(nextPage < totalPages);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="mobile-dashboard">
      {/* Stats Cards */}
      <div className="mobile-stats">
        <div 
          className="mobile-stats__card"
          onClick={() => navigate('/movies')}
        >
          <div className="mobile-stats__icon mobile-stats__icon--purple">
            <HiOutlineFilm size={24} />
          </div>
          <div className="mobile-stats__info">
            <span className="mobile-stats__value">{stats.movies}</span>
            <span className="mobile-stats__label">{t('dashboard.moviesInList')}</span>
          </div>
        </div>

        <div 
          className="mobile-stats__card"
          onClick={() => navigate('/series')}
        >
          <div className="mobile-stats__icon mobile-stats__icon--teal">
            <HiOutlineCollection size={24} />
          </div>
          <div className="mobile-stats__info">
            <span className="mobile-stats__value">{stats.series}</span>
            <span className="mobile-stats__label">{t('dashboard.seriesInList')}</span>
          </div>
        </div>
      </div>

      {/* Trending Section - Scroll Horizontal (igual Buscar) */}
      <section className="mobile-section">
        <div className="mobile-section__header">
          <HiOutlineTrendingUp size={20} />
          <h2>{t('dashboard.trendingThisWeek')}</h2>
        </div>

        {loading ? (
          <div className="mobile-loading">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* SCROLL HORIZONTAL - igual à página Buscar */}
            <div className="mobile-scroll-list">
              {trending.map((item) => (
                <div key={`${item.media_type}-${item.id}`} className="mobile-scroll-item">
                  <MediaCard item={item} onUpdate={loadData} />
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && trending.length > 0 && (
              <div className="mobile-load-more" style={{ marginTop: '16px', textAlign: 'center' }}>
                <IOSButton 
                  variant="default" 
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? t('common.loading') : t('dashboard.loadMore')}
                </IOSButton>
              </div>
            )}

            {!loading && trending.length === 0 && (
              <div className="mobile-empty">
                <div className="mobile-empty__icon">
                  <HiOutlineFilm size={48} />
                </div>
                <h3>{t('dashboard.noContentFound')}</h3>
                <p>{t('dashboard.configureApiKey')}</p>
              </div>
            )}
          </>
        )}
      </section>

      {/* Quick Actions */}
      <section className="mobile-section" style={{ marginTop: '24px' }}>
        <div className="mobile-section__header">
          <h2>{t('navigation.myList')}</h2>
        </div>
        <div className="mobile-quick-actions">
          <IOSButton 
            variant="default" 
            onClick={() => navigate('/movies')}
            icon={HiOutlineFilm}
            fullWidth
          >
            {t('movies.myMovies')}
          </IOSButton>
          <IOSButton 
            variant="default" 
            onClick={() => navigate('/series')}
            icon={HiOutlineCollection}
            fullWidth
          >
            {t('series.mySeries')}
          </IOSButton>
        </div>
      </section>

      {/* Bottom spacer */}
      <div className="mobile-dashboard__bottom-spacer" />
    </div>
  );
}

export default MobileDashboard;
